import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { fetchAuthSession, signOut } from 'aws-amplify/auth';

import { ThemeProvider } from '@mui/material/styles';

import ArticleIcon from '@mui/icons-material/Article';
import FolderIcon from '@mui/icons-material/Folder';
import SellIcon from '@mui/icons-material/Sell';
import LogoutIcon from '@mui/icons-material/Logout';
import PriceCheckIcon from '@mui/icons-material/PriceCheck';
import PhoneIcon from '@mui/icons-material/Phone';
import DescriptionIcon from '@mui/icons-material/Description';
import PeopleIcon from '@mui/icons-material/People';
import BarChartIcon from '@mui/icons-material/BarChart';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import PersonIcon from '@mui/icons-material/Person';
import AssignmentIcon from '@mui/icons-material/Assignment';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import ReceiptIcon from '@mui/icons-material/Receipt';
import HandshakeIcon from '@mui/icons-material/Handshake';

import adminTheme from './admin-theme';

import './customer-theme';

import Routes from './Routes';
import { AppContext } from './utils/content.util';
import {
  AppBar,
  Box,
  CssBaseline,
  Divider,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Typography,
  IconButton,
  Menu,
  MenuItem,
} from '@mui/material';
import Footer from './components/admin/footer/admin-footer.component';
import { hasPermission, Permission } from './models/role.model';
import { AwsCognitoUser } from './models/user.model';

const drawerWidth = 240;
const linkStyle = {
  textDecoration: 'none',
  color: 'inherit',
  width: '100%',
  '&:hover': {
    textDecoration: 'none',
  },
};

function showServiceItems(user: AwsCognitoUser) {
  return hasPermission(user, Permission.SERVICE_PAY);
}

function showManageQuotes(user: AwsCognitoUser) {
  return hasPermission(user, Permission.MANAGE_QUOTES);
}

function showCreateQuote(user: AwsCognitoUser) {
  return hasPermission(user, Permission.CREATE_QUOTE);
}

function showManageFees(user: AwsCognitoUser) {
  return hasPermission(user, Permission.MANAGE_FEES);
}

function showAdminSection(user: AwsCognitoUser) {
  return (
    hasPermission(user, Permission.VIEW_ANALYTIC_REPORT) ||
    hasPermission(user, Permission.MANAGE_DOCUMENTS) ||
    hasPermission(user, Permission.MANAGE_NEWS) ||
    hasPermission(user, Permission.MANAGE_USERS) ||
    hasPermission(user, Permission.MANAGE_ADMIN_DOCS)
  );
}

function showDealerRegistrationDocs(user: AwsCognitoUser) {
  return hasPermission(user, Permission.DEALER_REGISTRATION_DOCS);
}

function showContracting(user: AwsCognitoUser) {
  return hasPermission(user, Permission.CONTRACTING);
}

function App() {
  const navigate = useNavigate();
  const [isAuthenticated, userHasAuthenticated] = useState<boolean>(false);
  const [isAuthenticating, setIsAuthenticating] = useState<boolean>(true);
  const [currentUser, setCurrentUser] = useState<AwsCognitoUser | undefined>(undefined);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  useEffect(() => {
    onLoad();
  }, [isAuthenticated]);

  // Periodic check for user status (every 1 minute) while authenticated
  useEffect(() => {
    if (!isAuthenticated) {
      return;
    }

    const checkUserStatus = async () => {
      try {
        // Force refresh the session - if user is disabled, Cognito will reject this
        const session = await fetchAuthSession({ forceRefresh: true });

        // Check if session is valid
        if (!session.tokens || !session.userSub) {
          // No tokens means user is not authenticated (likely disabled)
          setCurrentUser(undefined);
          await signOut({ global: true });
          userHasAuthenticated(false);
          return;
        }
      } catch (e: any) {
        // If refresh fails (e.g., user is disabled), sign them out
        // Check error in various formats - Amplify errors can be in different shapes
        let errorType = e?.__type || e?.name || e?.code || e?.error || e?.underlyingError?.__type;
        let errorMessage = e?.message || e?.toString() || e?.underlyingError?.message || '';

        // Also check if error is a stringified JSON
        if (typeof e === 'string') {
          try {
            const parsedError = JSON.parse(e);
            if (parsedError.__type) {
              errorType = parsedError.__type;
            }
            if (parsedError.message) {
              errorMessage = parsedError.message;
            }
          } catch {
            // Not JSON, continue with original error
          }
        }

        // Check if error has underlyingError with the actual Cognito error
        if (e?.underlyingError) {
          const underlying = e.underlyingError;
          if (underlying.__type) {
            errorType = underlying.__type;
          }
          if (underlying.message) {
            errorMessage = underlying.message;
          }
        }

        // Any 400 error from Cognito when refreshing likely means user is disabled
        const isDisabledError =
          errorType === 'NotAuthorizedException' ||
          errorMessage?.toLowerCase().includes('disabled') ||
          errorMessage?.toLowerCase().includes('user is disabled') ||
          errorMessage?.toLowerCase().includes('notauthorized') ||
          errorMessage?.toLowerCase().includes('400') ||
          e?.statusCode === 400 ||
          e?.status === 400;

        if (isDisabledError) {
          // User is disabled or unauthorized, sign them out immediately
          setCurrentUser(undefined);
          await signOut({ global: true });
          userHasAuthenticated(false);
        } else {
          // Other errors (network, etc.) - log but don't sign out
          console.error('Failed to check user status:', e);
        }
      }
    };

    // Check immediately on mount/authentication
    checkUserStatus();

    // Set up interval to check every 1 minute (60000 ms)
    const intervalId = setInterval(checkUserStatus, 60 * 1000);

    // Cleanup interval on unmount or when authentication state changes
    return () => {
      clearInterval(intervalId);
    };
  }, [isAuthenticated]);

  async function onLoad() {
    try {
      const fetchSessionResult = await fetchAuthSession();

      if (fetchSessionResult.userSub) {
        setCurrentUser(fetchSessionResult.tokens?.idToken?.payload as AwsCognitoUser);
        userHasAuthenticated(true);
      }
    } catch (e) {
      if (e !== 'No current user') {
        alert(e);
      }
    }

    setIsAuthenticating(false);
  }

  async function handleLogout(e: any) {
    e.preventDefault();

    setCurrentUser(undefined);
    setAnchorEl(null);
    await signOut({ global: true });

    userHasAuthenticated(false);
  }

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleProfileClick = () => {
    handleMenuClose();
    // Navigate to profile page
    navigate('/profile');
  };

  function authContainer() {
    return (
      <ThemeProvider theme={adminTheme}>
        {isAuthenticated ? (
          <Box sx={{ display: 'flex' }}>
            <CssBaseline />

            <AppBar
              position="fixed"
              color="primary"
              enableColorOnDark
              sx={{
                width: `calc(100% - ${drawerWidth}px)`,
                ml: `${drawerWidth}px`,
                height: '64px',
                backgroundColor: '#0a2543',
                padding: '0 16px',
              }}
            >
              <Toolbar
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  height: '100%', //
                }}
              >
                <Typography variant="h6" noWrap component="div"></Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body1" sx={{ color: 'white' }}>
                    {`${currentUser?.given_name ?? ''} ${currentUser?.family_name ?? ''}` || 'User'}
                  </Typography>
                  <IconButton
                    onClick={handleMenuOpen}
                    sx={{ color: 'white' }}
                    aria-label="account menu"
                    aria-controls="account-menu"
                    aria-haspopup="true"
                  >
                    <AccountCircleIcon />
                  </IconButton>
                  <Menu
                    id="account-menu"
                    anchorEl={anchorEl}
                    open={Boolean(anchorEl)}
                    onClose={handleMenuClose}
                    anchorOrigin={{
                      vertical: 'bottom',
                      horizontal: 'right',
                    }}
                    transformOrigin={{
                      vertical: 'top',
                      horizontal: 'right',
                    }}
                  >
                    <MenuItem onClick={handleProfileClick}>
                      <PersonIcon sx={{ mr: 1, fontSize: 20 }} />
                      Profile
                    </MenuItem>
                    <MenuItem onClick={handleLogout}>
                      <LogoutIcon sx={{ mr: 1, fontSize: 20 }} />
                      Logout
                    </MenuItem>
                  </Menu>
                </Box>
              </Toolbar>
            </AppBar>

            <Drawer
              color="primary"
              sx={{
                width: drawerWidth,
                flexShrink: 0,
                '& .MuiDrawer-paper': {
                  width: drawerWidth,
                  boxSizing: 'border-box',
                  backgroundColor: '#eee',
                  borderRight: 'none',
                },
              }}
              variant="permanent"
              anchor="left"
              id="dash-nav"
            >
              <Toolbar
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  backgroundColor: '#0a2543',
                }}
              >
                <Link to="/dashboard" style={{ textDecoration: 'none', cursor: 'pointer' }}>
                  <Box
                    component="img"
                    sx={{
                      height: 40,
                      width: 'auto',
                      cursor: 'pointer',
                    }}
                    alt="Logo"
                    src="/assets/images/tsf-logo-white.png"
                  />
                </Link>
              </Toolbar>

              <Divider />

              <List>
                <ListItem>
                  <Typography variant="subtitle2" color="textSecondary" sx={{ fontWeight: 600 }}>
                    Financing
                  </Typography>
                </ListItem>

                <ListItem disablePadding>
                  <Link to="/service-forms" style={linkStyle}>
                    <ListItemButton sx={{ '&:hover': { backgroundColor: 'transparent' } }}>
                      <ListItemIcon sx={{ minWidth: '35px' }}>
                        <ArticleIcon fontSize="small" />
                      </ListItemIcon>
                      <ListItemText primary={'Service Forms'} slotProps={{ primary: { sx: { fontWeight: 500 } } }} />
                    </ListItemButton>
                  </Link>
                </ListItem>

                <ListItem disablePadding>
                  <Link to="/phone" style={linkStyle}>
                    <ListItemButton sx={{ '&:hover': { backgroundColor: 'transparent' } }}>
                      <ListItemIcon sx={{ minWidth: '35px' }}>
                        <PhoneIcon fontSize="small" />
                      </ListItemIcon>
                      <ListItemText
                        primary={'Create Service Form'}
                        slotProps={{ primary: { sx: { fontWeight: 500 } } }}
                      />
                    </ListItemButton>
                  </Link>
                </ListItem>

                {/* {currentUser && hasPermission(currentUser, Permission.COMMERCIAL_FORMS) && (
                  <ListItem disablePadding>
                    <Link to="/commercial-forms" style={linkStyle}>
                      <ListItemButton sx={{ '&:hover': { backgroundColor: 'transparent' } }}>
                        <ListItemIcon sx={{ minWidth: '35px' }}>
                          <AccountBalanceIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText
                          primary={'Commercial Forms'}
                          slotProps={{ primary: { sx: { fontWeight: 500 } } }}
                        />
                      </ListItemButton>
                    </Link>
                  </ListItem>
                )} */}

                <ListItem>
                  <Typography variant="subtitle2" color="textSecondary" sx={{ fontWeight: 600 }}>
                    Dealers
                  </Typography>
                </ListItem>

                <ListItem disablePadding>
                  <Link to="/dealers" style={linkStyle}>
                    <ListItemButton sx={{ '&:hover': { backgroundColor: 'transparent' } }}>
                      <ListItemIcon sx={{ minWidth: '35px' }}>
                        <SellIcon fontSize="small" />
                      </ListItemIcon>
                      <ListItemText primary={'Dealers'} slotProps={{ primary: { sx: { fontWeight: 500 } } }} />
                    </ListItemButton>
                  </Link>
                </ListItem>

                {currentUser && hasPermission(currentUser, Permission.MANAGE_DEALERS_V2) && (
                  <ListItem disablePadding>
                    <Link to="/dealers-v2" style={linkStyle}>
                      <ListItemButton sx={{ '&:hover': { backgroundColor: 'transparent' } }}>
                        <ListItemIcon sx={{ minWidth: '35px' }}>
                          <SellIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText primary={'Dealers (v2)'} slotProps={{ primary: { sx: { fontWeight: 500 } } }} />
                      </ListItemButton>
                    </Link>
                  </ListItem>
                )}

                {currentUser &&
                  (hasPermission(currentUser, Permission.MANAGE_DEALER_PROSPECTS) ||
                    hasPermission(currentUser, Permission.MANAGE_MY_LEADS)) && (
                    <>
                      <ListItem>
                        <Typography variant="subtitle2" color="textSecondary" sx={{ fontWeight: 600 }}>
                          Prospects
                        </Typography>
                      </ListItem>

                      {hasPermission(currentUser, Permission.MANAGE_DEALER_PROSPECTS) && (
                        <ListItem disablePadding>
                          <Link to="/dealer-prospects" style={linkStyle}>
                            <ListItemButton sx={{ '&:hover': { backgroundColor: 'transparent' } }}>
                              <ListItemIcon sx={{ minWidth: '35px' }}>
                                <PeopleIcon fontSize="small" />
                              </ListItemIcon>
                              <ListItemText
                                primary={'Dealer Prospects'}
                                slotProps={{ primary: { sx: { fontWeight: 500 } } }}
                              />
                            </ListItemButton>
                          </Link>
                        </ListItem>
                      )}

                      {hasPermission(currentUser, Permission.MANAGE_MY_LEADS) && (
                        <ListItem disablePadding>
                          <Link to="/my-leads" style={linkStyle}>
                            <ListItemButton sx={{ '&:hover': { backgroundColor: 'transparent' } }}>
                              <ListItemIcon sx={{ minWidth: '35px' }}>
                                <PersonIcon fontSize="small" />
                              </ListItemIcon>
                              <ListItemText
                                primary={'My Leads'}
                                slotProps={{ primary: { sx: { fontWeight: 500 } } }}
                              />
                            </ListItemButton>
                          </Link>
                        </ListItem>
                      )}
                    </>
                  )}

                {currentUser ? (
                  <>
                    <ListItem>
                      <Typography variant="subtitle2" color="textSecondary" sx={{ fontWeight: 600 }}>
                        Resources
                      </Typography>
                    </ListItem>

                    {currentUser && showServiceItems(currentUser) && (
                      <ListItem disablePadding>
                        <Link to="/payments" style={linkStyle}>
                          <ListItemButton sx={{ '&:hover': { backgroundColor: 'transparent' } }}>
                            <ListItemIcon sx={{ minWidth: '35px' }}>
                              <PriceCheckIcon fontSize="small" />
                            </ListItemIcon>
                            <ListItemText primary={'Payments'} slotProps={{ primary: { sx: { fontWeight: 500 } } }} />
                          </ListItemButton>
                        </Link>
                      </ListItem>
                    )}

                    {currentUser && showContracting(currentUser) && (
                      <ListItem disablePadding>
                        <Link to="/admin-contracting" style={linkStyle}>
                          <ListItemButton sx={{ '&:hover': { backgroundColor: 'transparent' } }}>
                            <ListItemIcon sx={{ minWidth: '35px' }}>
                              <HandshakeIcon fontSize="small" />
                            </ListItemIcon>
                            <ListItemText primary={'Contracting'} slotProps={{ primary: { sx: { fontWeight: 500 } } }} />
                          </ListItemButton>
                        </Link>
                      </ListItem>
                    )}

                    {currentUser && showDealerRegistrationDocs(currentUser) && (
                      <ListItem disablePadding>
                        <Link to="/dealer-documents" style={linkStyle}>
                          <ListItemButton sx={{ '&:hover': { backgroundColor: 'transparent' } }}>
                            <ListItemIcon sx={{ minWidth: '35px' }}>
                              <ArticleIcon fontSize="small" />
                            </ListItemIcon>
                            <ListItemText
                              primary={'Dealer Documents'}
                              slotProps={{ primary: { sx: { fontWeight: 500 } } }}
                            />
                          </ListItemButton>
                        </Link>
                      </ListItem>
                    )}

                    <ListItem disablePadding>
                      <Link to="/onboarding" style={linkStyle}>
                        <ListItemButton sx={{ '&:hover': { backgroundColor: 'transparent' } }}>
                          <ListItemIcon sx={{ minWidth: '35px' }}>
                            <FolderIcon fontSize="small" />
                          </ListItemIcon>
                          <ListItemText
                            primary={'Onboarding'}
                            slotProps={{ primary: { sx: { fontWeight: 500 } } }}
                          />
                        </ListItemButton>
                      </Link>
                    </ListItem>
                  </>
                ) : undefined}
              </List>

              {currentUser &&
              (showManageQuotes(currentUser) || showCreateQuote(currentUser) || showManageFees(currentUser)) ? (
                <>
                  <Divider />

                  <List>
                    <ListItem>
                      <Typography variant="subtitle2" color="textSecondary" sx={{ fontWeight: 600 }}>
                        Title & Registration
                      </Typography>
                    </ListItem>

                    {currentUser && showManageQuotes(currentUser) && (
                      <ListItem disablePadding>
                        <Link to="/quotes" style={linkStyle}>
                          <ListItemButton sx={{ '&:hover': { backgroundColor: 'transparent' } }}>
                            <ListItemIcon sx={{ minWidth: '35px' }}>
                              <AssignmentIcon fontSize="small" />
                            </ListItemIcon>
                            <ListItemText
                              primary={'Manage Quotes'}
                              slotProps={{ primary: { sx: { fontWeight: 500 } } }}
                            />
                          </ListItemButton>
                        </Link>
                      </ListItem>
                    )}

                    {currentUser && showCreateQuote(currentUser) && (
                      <ListItem disablePadding>
                        <Link to="/quotes/new" style={linkStyle}>
                          <ListItemButton sx={{ '&:hover': { backgroundColor: 'transparent' } }}>
                            <ListItemIcon sx={{ minWidth: '35px' }}>
                              <ReceiptIcon fontSize="small" />
                            </ListItemIcon>
                            <ListItemText
                              primary={'Create Quote'}
                              slotProps={{ primary: { sx: { fontWeight: 500 } } }}
                            />
                          </ListItemButton>
                        </Link>
                      </ListItem>
                    )}

                    {currentUser && showManageFees(currentUser) && (
                      <ListItem disablePadding>
                        <Link to="/fees" style={linkStyle}>
                          <ListItemButton sx={{ '&:hover': { backgroundColor: 'transparent' } }}>
                            <ListItemIcon sx={{ minWidth: '35px' }}>
                              <AttachMoneyIcon fontSize="small" />
                            </ListItemIcon>
                            <ListItemText
                              primary={'Manage Fees'}
                              slotProps={{ primary: { sx: { fontWeight: 500 } } }}
                            />
                          </ListItemButton>
                        </Link>
                      </ListItem>
                    )}
                  </List>
                </>
              ) : undefined}

              {currentUser && showAdminSection(currentUser) ? (
                <>
                  <Divider />

                  <List>
                    <ListItem>
                      <Typography variant="subtitle2" color="textSecondary" sx={{ fontWeight: 600 }}>
                        Admin
                      </Typography>
                    </ListItem>

                    {currentUser && hasPermission(currentUser, Permission.VIEW_ANALYTIC_REPORT) && (
                      <ListItem disablePadding>
                        <Link to="/analytic-report" style={linkStyle}>
                          <ListItemButton sx={{ '&:hover': { backgroundColor: 'transparent' } }}>
                            <ListItemIcon sx={{ minWidth: '35px' }}>
                              <BarChartIcon fontSize="small" />
                            </ListItemIcon>
                            <ListItemText
                              primary={'Analytic Report'}
                              slotProps={{ primary: { sx: { fontWeight: 500 } } }}
                            />
                          </ListItemButton>
                        </Link>
                      </ListItem>
                    )}

                    {currentUser && hasPermission(currentUser, Permission.MANAGE_DOCUMENTS) && (
                      <ListItem disablePadding>
                        <Link to="/documents" style={linkStyle}>
                          <ListItemButton sx={{ '&:hover': { backgroundColor: 'transparent' } }}>
                            <ListItemIcon sx={{ minWidth: '35px' }}>
                              <DescriptionIcon fontSize="small" />
                            </ListItemIcon>
                            <ListItemText
                              primary={'Manage Docs'}
                              slotProps={{ primary: { sx: { fontWeight: 500 } } }}
                            />
                          </ListItemButton>
                        </Link>
                      </ListItem>
                    )}

                    {currentUser && hasPermission(currentUser, Permission.MANAGE_ADMIN_DOCS) && (
                      <ListItem disablePadding>
                        <Link to="/admin-docs" style={linkStyle}>
                          <ListItemButton sx={{ '&:hover': { backgroundColor: 'transparent' } }}>
                            <ListItemIcon sx={{ minWidth: '35px' }}>
                              <ArticleIcon fontSize="small" />
                            </ListItemIcon>
                            <ListItemText primary={'Admin Docs'} slotProps={{ primary: { sx: { fontWeight: 500 } } }} />
                          </ListItemButton>
                        </Link>
                      </ListItem>
                    )}

                    {currentUser && hasPermission(currentUser, Permission.MANAGE_NEWS) && (
                      <ListItem disablePadding>
                        <Link to="/news" style={linkStyle}>
                          <ListItemButton sx={{ '&:hover': { backgroundColor: 'transparent' } }}>
                            <ListItemIcon sx={{ minWidth: '35px' }}>
                              <ArticleIcon fontSize="small" />
                            </ListItemIcon>
                            <ListItemText
                              primary={'Manage News'}
                              slotProps={{ primary: { sx: { fontWeight: 500 } } }}
                            />
                          </ListItemButton>
                        </Link>
                      </ListItem>
                    )}

                    {currentUser && hasPermission(currentUser, Permission.MANAGE_USERS) && (
                      <ListItem disablePadding>
                        <Link to="/users" style={linkStyle}>
                          <ListItemButton sx={{ '&:hover': { backgroundColor: 'transparent' } }}>
                            <ListItemIcon sx={{ minWidth: '35px' }}>
                              <PeopleIcon fontSize="small" />
                            </ListItemIcon>
                            <ListItemText
                              primary={'Manage Users'}
                              slotProps={{ primary: { sx: { fontWeight: 500 } } }}
                            />
                          </ListItemButton>
                        </Link>
                      </ListItem>
                    )}
                  </List>
                </>
              ) : undefined}

              {/* <Divider />

              <List>
                <ListItem disablePadding>
                  <LinkContainer to="/manage-staff">
                    <ListItemButton>
                      <ListItemIcon>
                        <PeopleIcon fontSize="small" />
                      </ListItemIcon>
                      <ListItemText primary={"Staff"} />
                    </ListItemButton>
                  </LinkContainer>
                </ListItem>

                <ListItem disablePadding>
                  <LinkContainer to="/manage-faq">
                    <ListItemButton>
                      <ListItemIcon>
                        <LiveHelpIcon fontSize="small" />
                      </ListItemIcon>
                      <ListItemText primary={"FAQ"} />
                    </ListItemButton>
                  </LinkContainer>
                </ListItem>
              </List>

              <Divider /> */}

              {/* <List>
                <ListItem disablePadding>
                  <Link to="" onClick={handleLogout} style={linkStyle}>
                    <ListItemButton sx={{ '&:hover': { backgroundColor: 'transparent' } }}>
                      <ListItemIcon sx={{ minWidth: '35px' }}>
                        <LogoutIcon fontSize="small" />
                      </ListItemIcon>
                      <ListItemText primary={'Logout'} slotProps={{ primary: { sx: { fontWeight: 500 } } }} />
                    </ListItemButton>
                  </Link>
                </ListItem>
              </List> */}
            </Drawer>

            <Box component="main" sx={{ flexGrow: 1, bgcolor: 'background.default', p: 3 }}>
              <Toolbar>
                <Typography variant="h6" noWrap component="div"></Typography>
              </Toolbar>

              <AppContext.Provider value={{ isAuthenticated, userHasAuthenticated, currentUser }}>
                <Routes />
              </AppContext.Provider>
            </Box>
          </Box>
        ) : (
          <div>
            <AppContext.Provider value={{ isAuthenticated, userHasAuthenticated }}>
              <Routes />
            </AppContext.Provider>
          </div>
        )}
        {isAuthenticated && <Footer />}
      </ThemeProvider>
    );
  }
  return !isAuthenticating ? authContainer() : <></>;
}

export default App;
